import { FunctionComponent, useCallback, useEffect, useState } from 'react'
import styled, { css } from 'styled-components'
import { useRouter } from 'next/router'

import { type Image } from '@utils/cook/types'
import { CookWidget, JSONTypeForCookWidget } from '@widgets/types'
import { mobileCSS } from '@measures/responsive'

import useIsAuthResolved from '@hooks/useIsAuthResolved'
import useFeatureFlagsSkeleton from '@hooks/useFeatureFlagsSkeleton'
import useUser from '@hooks/useUser'

import FollowMyTeamOverviewComponent from '@components/FollowMyTeamOverview'
import FollowMyTeamOverviewBanner from '@components/FollowMyTeamOverview/FollowMyTeamOverviewBanner'
import FollowMyTeamDrawer from '@components/FollowMyTeam/FollowMyTeamDrawer'
import FollowMyTeamMyTeams from '@components/FollowMyTeam/FollowMyTeamMyTeams'
import FollowMyTeamSelectTeam from '@components/FollowMyTeam/FollowMyTeamSelectTeam'
import {
  getFmTDrawerValueFromHash,
  pathWithFmTDrawerHash,
  pathWithoutFmTDrawerHash,
} from '@components/FollowMyTeam/FollowMyTeamDrawer/utils'

export interface FollowMyTeamOverviewAPIProps {
  title: string
  backgroundImage: Image<'16_9'>
  image: Image<'free'>
}

const FollowMyTeamOverviewWrapper = styled.div`
  height: 80px;

  ${mobileCSS(css`
    height: 64px;
  `)};
`

const FollowMyTeamOverview: FunctionComponent<FollowMyTeamOverviewAPIProps> = (
  props
) => {
  const { title, backgroundImage, image } = props

  const router = useRouter()
  const { isFollowMyTeamEnabled } = useFeatureFlagsSkeleton()
  const user = useUser()
  const isAuthResolved = useIsAuthResolved()

  const [isFollowMyTeamDrawerOpen, setIsFollowMyTeamDrawerOpen] =
    useState(false)
  const [showMyTeams, setShowMyTeams] = useState(false)
  const [showSelectTeam, setShowSelectTeam] = useState(false)

  const addFmTDrawerHashToPath = useCallback(() => {
    const routeAsPath = router.asPath
    const newRouteAsPath = pathWithFmTDrawerHash(routeAsPath, 'true')

    if (routeAsPath !== newRouteAsPath) {
      router.replace(newRouteAsPath, undefined, {
        shallow: true,
        scroll: false,
      })
    }
  }, [router])

  const removeFmTDrawerHashFromPath = useCallback(() => {
    const routeAsPath = router.asPath
    const newRouteAsPath = pathWithoutFmTDrawerHash(routeAsPath)

    if (routeAsPath !== newRouteAsPath) {
      router.replace(newRouteAsPath, undefined, {
        shallow: true,
        scroll: false,
      })
    }
  }, [router])

  useEffect(() => {
    const routeAsPath = router.asPath
    const fmTDrawerValue = getFmTDrawerValueFromHash(routeAsPath)

    if (fmTDrawerValue) {
      setShowSelectTeam(true)
      setIsFollowMyTeamDrawerOpen(true)
    }
  }, [router])

  const handleBannerClick = useCallback(() => {
    setShowSelectTeam(true)
    setIsFollowMyTeamDrawerOpen(true)
    addFmTDrawerHashToPath()
  }, [addFmTDrawerHashToPath])

  const handleSettingsClick = useCallback(() => {
    setShowMyTeams(true)
    setIsFollowMyTeamDrawerOpen(true)
    addFmTDrawerHashToPath()
  }, [addFmTDrawerHashToPath])

  const handleAddTeamClick = useCallback(() => {
    setShowSelectTeam(true)
    setIsFollowMyTeamDrawerOpen(true)
    addFmTDrawerHashToPath()
  }, [addFmTDrawerHashToPath])

  const handleCloseDrawer = useCallback(() => {
    setIsFollowMyTeamDrawerOpen(false)
    setShowSelectTeam(false)
    setShowMyTeams(false)
    removeFmTDrawerHashFromPath()
  }, [removeFmTDrawerHashFromPath])

  if (!isFollowMyTeamEnabled) {
    return null
  }

  return (
    <FollowMyTeamOverviewWrapper>
      {isAuthResolved ? (
        !!user ? (
          <FollowMyTeamOverviewComponent
            backgroundImage={backgroundImage}
            image={image}
            title={title}
            onBannerClick={handleBannerClick}
            onSettingsClick={handleSettingsClick}
            onAddTeamClick={handleAddTeamClick}
          />
        ) : (
          <FollowMyTeamOverviewBanner
            state="notLoggedIn"
            backgroundImage={backgroundImage}
            image={image}
            title={title}
            onClick={handleBannerClick}
          />
        )
      ) : null}
      {
        <FollowMyTeamDrawer
          open={isFollowMyTeamDrawerOpen}
          onClose={handleCloseDrawer}>
          {showSelectTeam && (
            <FollowMyTeamSelectTeam onClose={handleCloseDrawer} />
          )}
          {showMyTeams && <FollowMyTeamMyTeams onClose={handleCloseDrawer} />}
        </FollowMyTeamDrawer>
      }
    </FollowMyTeamOverviewWrapper>
  )
}

const widget = {
  kind: ['follow-my-team-overview'],
  component: FollowMyTeamOverview,
} as const satisfies CookWidget

export type WidgetType = typeof widget

export type JSONWidgetType = JSONTypeForCookWidget<WidgetType>

export default widget
